import React, { useState, useEffect } from 'react';
import UsersService, { BOAPI } from '../service/UsersService';
import { useNavigate } from "react-router-dom";

const UsersTab = () => {
  const navigate = useNavigate();

  const [users, setUsers]           = useState([]);
  const [pending, setPending]       = useState([]);
  const [activeSection, setActiveSection] = useState('pending'); // 'pending' | 'all'
  const [loading, setLoading]       = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [error, setError]           = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    loadPending();
    loadUsers();
  }, []);

  const loadPending = async () => {
    try {
      setPendingLoading(true);
      const resp = await BOAPI.fetchPendingUsers();
      if (resp?.[0]?.[0]?.code === 0) setPending(resp[1] || []);
    } catch { /* ignore */ }
    finally { setPendingLoading(false); }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await UsersService.getUsers(async () => {
        const resp = await BOAPI.fetchUsers();
        if (resp?.[0]?.[0]?.code === 0) return resp[1] || [];
        throw new Error();
      });
      setUsers(data);
    } catch {
      setError('שגיאה בטעינת המשתמשים');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (user) => {
    if (!window.confirm(`לאשר את ההרשמה של ${user.fName} ${user.lName}?`)) return;
    try {
      const resp = await BOAPI.approveUser(user.id);
      if (resp?.[0]?.[0]?.code === 0) {
        setPending(prev => prev.filter(u => u.id !== user.id));
        setSuccessMsg(`✅ ${user.fName} ${user.lName} אושרה בהצלחה!`);
        setTimeout(() => setSuccessMsg(''), 4000);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שגיאה באישור');
      }
    } catch {
      setError('שגיאה באישור המשתמש');
    }
  };

  const fmtDate = (d) => d
    ? new Date(d).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>ניהול משתמשים</h2>
        <div style={s.sectionToggle}>
          <button
            style={{ ...s.toggleBtn, ...(activeSection === 'pending' ? s.toggleActive : {}) }}
            onClick={() => setActiveSection('pending')}
          >
            ⏳ ממתינים לאישור
            {pending.length > 0 && <span style={s.badge}>{pending.length}</span>}
          </button>
          <button
            style={{ ...s.toggleBtn, ...(activeSection === 'all' ? s.toggleActive : {}) }}
            onClick={() => setActiveSection('all')}
          >
            👥 כל המשתמשים ({users.length})
          </button>
        </div>
      </div>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.successBanner}>{successMsg}</div>}

      {/* ── ממתינים לאישור ── */}
      {activeSection === 'pending' && (
        <>
          {pendingLoading ? (
            <div style={s.center}>טוען...</div>
          ) : pending.length === 0 ? (
            <div style={s.emptyState}>
              <div style={{ fontSize: 48 }}>✅</div>
              <p style={{ color: '#697386', fontWeight: 600 }}>אין בקשות הרשמה ממתינות</p>
            </div>
          ) : (
            <div style={s.pendingGrid}>
              {pending.map(user => (
                <div key={user.id} style={s.pendingCard}>
                  <div style={s.pendingAvatar}>
                    {(user.fName || '?')[0]}{(user.lName || '')[0]}
                  </div>
                  <div style={s.pendingInfo}>
                    <div style={s.pendingName}>{user.fName} {user.lName}</div>
                    <div style={s.pendingMeta}>@{user.username}</div>
                    {user.email && <div style={s.pendingMeta}>✉️ {user.email}</div>}
                    {user.phone && <div style={s.pendingMeta}>📱 {user.phone}</div>}
                    {user.hebDate && <div style={s.pendingMeta}>🎂 {user.hebDate}</div>}
                    <div style={s.pendingMeta}>🕐 נרשם: {fmtDate(user.iTime)}</div>
                    {user.hasPushToken
                      ? <div style={{ ...s.pendingMeta, color: '#34D399' }}>🔔 הסכים לקבל הודעות</div>
                      : <div style={{ ...s.pendingMeta, color: '#94A3B8' }}>🔕 לא הסכים להודעות</div>
                    }
                  </div>
                  <button
                    style={s.approveBtn}
                    onClick={() => handleApprove(user)}
                  >
                    ✅ אשר הרשמה
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── כל המשתמשים ── */}
      {activeSection === 'all' && (
        <>
          {loading ? (
            <div style={s.center}>טוען משתמשים...</div>
          ) : (
            <div style={s.tableWrapper}>
              <table style={s.table}>
                <thead>
                  <tr style={s.headerRow2}>
                    <th style={s.th}>ID</th>
                    <th style={s.th}>שם</th>
                    <th style={s.th}>שם משתמש</th>
                    <th style={s.th}>אימייל</th>
                    <th style={s.th}>תפקיד</th>
                    <th style={s.th}>נרשם</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr
                      key={user.id}
                      style={s.row}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                    >
                      <td style={s.td}><span style={s.idText}>{user.id}</span></td>
                      <td style={s.td}>
                        <button
                          style={s.nameLink}
                          onClick={() => navigate(`/users/${user.id}`)}
                        >
                          {user.fName} {user.lName}
                        </button>
                      </td>
                      <td style={s.td}>{user.username || '-'}</td>
                      <td style={s.td}>{user.email || '-'}</td>
                      <td style={s.td}>
                        <span style={{
                          ...s.roleBadge,
                          background: user.role === 'admin' ? '#FEF9EE' : '#EEF2FF',
                          color: user.role === 'admin' ? '#C9A84C' : '#1f6feb',
                        }}>
                          {user.role || 'guest'}
                        </span>
                      </td>
                      <td style={s.td}>{fmtDate(user.iTime)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  sectionToggle:{ display: 'flex', gap: 8 },
  toggleBtn:    { padding: '8px 16px', borderRadius: 20, border: '1.5px solid #e3e8ee', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#697386', display: 'flex', alignItems: 'center', gap: 6 },
  toggleActive: { background: '#1f6feb', borderColor: '#1f6feb', color: '#fff' },
  badge:        { background: '#EF4444', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, marginRight: 2 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  successBanner:{ background: '#e8f5e9', color: '#2e7d32', padding: 14, borderRadius: 6, marginBottom: 16, fontWeight: 600 },
  emptyState:   { textAlign: 'center', padding: '60px 0' },

  // Pending cards
  pendingGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 },
  pendingCard:  { background: '#fff', borderRadius: 16, border: '1px solid #e3e8ee', padding: 20, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  pendingAvatar:{ width: 52, height: 52, borderRadius: '50%', background: '#EEF2FF', color: '#1f6feb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 'bold', alignSelf: 'flex-start' },
  pendingInfo:  { flex: 1, display: 'flex', flexDirection: 'column', gap: 4 },
  pendingName:  { fontSize: 16, fontWeight: 700, color: '#1a1f36' },
  pendingMeta:  { fontSize: 12.5, color: '#697386' },
  approveBtn:   { padding: '10px 16px', background: '#22C55E', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, marginTop: 'auto' },

  // All users table
  tableWrapper: { overflowX: 'auto', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  table:        { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow2:   { backgroundColor: '#f5f7fa', borderBottom: '2px solid #e3e8ee' },
  th:           { padding: '14px 16px', textAlign: 'right', fontWeight: 700, color: '#4f566b', fontSize: 13, borderBottom: '2px solid #e3e8ee' },
  row:          { borderBottom: '1px solid #f0f2f5', cursor: 'pointer', transition: 'background 0.15s' },
  td:           { padding: '14px 16px', color: '#4f566b', fontSize: 13, textAlign: 'right' },
  idText:       { fontWeight: 700, color: '#a3acb9' },
  nameLink:     { background: 'none', border: 'none', cursor: 'pointer', color: '#1f6feb', fontWeight: 700, fontSize: 13, padding: 0 },
  roleBadge:    { display: 'inline-block', borderRadius: 12, padding: '3px 10px', fontSize: 11, fontWeight: 700 },
};

export default UsersTab;
